// MarkerDetail.tsx (o .jsx)
import * as React from 'react';
import { Autocomplete, Box, Button, Collapse, Divider, Grid, IconButton, Paper, TextField, Typography, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import Loading from '../../../../auxiliares/loading';
import { black, ButtonBrand, gold, white } from '../../../../getLPTheme';
import { options } from '@config';

type Item = {
  pointId: string;
  name: string;
  typeMarker: string;
  address: string;
  country: string;
  state: string;
  locate: string;
  contact: string;
  url: string;
  coordinates: string;
  discount: string;
};

export default function MarkerDetail({ items }: { items: Item[] }) {
  // estilos responsive
  const paperStyle = {
    width: '100%',
    maxWidth: 560,
    // en mobile no recortamos; en sm+ sí permitimos scroll interno si hace falta
    maxHeight: { xs: 'unset', sm: '80vh' },
    overflow: { xs: 'visible', sm: 'auto' },
    backgroundColor: black[1000],
    p: { xs: 0, sm: 0 },
  } as const;

  const containerSx = {
    bgcolor: black[1000],
    width: '100%',
    // el padre (página) ya lleva minHeight: 100vh; acá solo respiramos
    p: { xs: 0, sm: 0 },
  } as const;

  const tfSx = {
    width: { xs: '100%', sm: 260 },
    pr: { xs: 0, sm: 1 },
    color: white[50],
    '& .MuiOutlinedInput-root': {
      "&.Mui-focused fieldset": { borderColor: white[50] },
      boxSizing: 'border-box',
      borderRadius: '0px',
      border: '1px solid',
      borderColor: white[50],
      color: white[50],
      '&:hover': { borderColor: gold[500], color: white[50] },
    },
  } as const;

  const typographyStyle = { color: white[50] };
  const params = useParams();
  const navigate = useNavigate();

  const searchedRowData = items.find((row) => row.pointId === params.mid) ?? ({} as Partial<Item>);

  const [formData, setFormData] = React.useState({
    pointId: searchedRowData.pointId ?? '',
    name: searchedRowData.name ?? '',
    typeMarker: searchedRowData.typeMarker ?? '',
    address: searchedRowData.address ?? '',
    country: searchedRowData.country ?? '',
    state: searchedRowData.state ?? '',
    locate: searchedRowData.locate ?? '',
    contact: searchedRowData.contact ?? '',
    url: searchedRowData.url ?? '',
    coordinates: searchedRowData.coordinates ?? '',
    discount: searchedRowData.discount ?? '',
  });

  const [openWarning, setopenWarning] = React.useState(false);
  const [openButtons, setopenButtons] = React.useState(true);
  const [openSuccess, setopenSuccess] = React.useState(false);
  const [openProgress, setopenProgress] = React.useState(false);
  const [response, setResponse] = React.useState({});
  const [openError, setopenError] = React.useState(false);
  const [action, setAction] = React.useState<'Editar' | 'Borrar' | 'Editar/borrar'>('Editar/borrar');
  const [actionSuccess, setActionSuccess] = React.useState<'editado' | 'borrado' | 'Editado/Borrado'>('Editado/Borrado');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleTypeMarkerChange = (_: unknown, value: string | null) => {
    setFormData((prev) => ({ ...prev, typeMarker: value ?? '' }));
  };

  const handleSubmit = async () => {
    setopenProgress(true);
    try {
      if (action === 'Borrar') {
        const response = await fetch(options.api.url + 'pointid' + `?pid=${params.mid}`, {
          method: 'DELETE',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await response.json().catch(() => ({}));
        setResponse(data);
        setopenSuccess(response.status === 200);
        setopenError(response.status !== 200);
      } else {
        const response = await fetch(options.api.url + 'pointid' + `?pid=${params.mid}`, {
          method: 'PUT',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        const data = await response.json().catch(() => ({}));
        setResponse(data);
        setopenSuccess(response.status === 200);
        setopenError(response.status !== 200);
      }
    } finally {
      setopenProgress(false);
    }
  };

  return (
    <Box sx={containerSx}>
      <Paper elevation={24} sx={paperStyle}>
        <Box sx={{ p: { xs: 10, sm: 2 } }}>
          <Divider />
          <form onSubmit={(e) => e.preventDefault()}>
            <Grid
              container
              spacing={{ xs: 1, sm: 1.5 }}
              alignItems="center"
              justifyContent="center"
            >
              {/* Nombre */}
              <Grid item xs={12} sm={4}>
                <Typography sx={typographyStyle}>(*)Nombre:</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  margin="dense"
                  size="small"
                  label=""
                  placeholder="name"
                  required
                  sx={tfSx}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </Grid>

              {/* Tipo de Marcador */}
              <Grid item xs={12} sm={4}>
                <Typography sx={typographyStyle}>(*)Tipo de Marcador:</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  options={['club', 'restaurant', 'hotel', 'comercio', 'otros']}
                  value={formData.typeMarker || null}
                  onChange={handleTypeMarkerChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      margin="dense"
                      label=""
                      required
                      placeholder="Tipo de Marcador"
                      sx={tfSx}
                    />
                  )}
                />
              </Grid>

              {/* Dirección */}
              <Grid item xs={12} sm={4}>
                <Typography sx={typographyStyle}>(*)Dirección:</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  margin="dense"
                  size="small"
                  label=""
                  placeholder="Dirección"
                  required
                  sx={tfSx}
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                />
              </Grid>

              {/* País */}
              <Grid item xs={12} sm={4}>
                <Typography sx={typographyStyle}>(*)País:</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  margin="dense"
                  size="small"
                  label=""
                  placeholder="País"
                  required
                  sx={tfSx}
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                />
              </Grid>

              {/* Estado */}
              <Grid item xs={12} sm={4}>
                <Typography sx={typographyStyle}>(*)Estado:</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  margin="dense"
                  size="small"
                  label=""
                  placeholder="Estado"
                  required
                  sx={tfSx}
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                />
              </Grid>

              {/* Localidad */}
              <Grid item xs={12} sm={4}>
                <Typography sx={typographyStyle}>(*)Localidad:</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  margin="dense"
                  size="small"
                  label=""
                  placeholder="Localidad"
                  required
                  sx={tfSx}
                  name="locate"
                  value={formData.locate}
                  onChange={handleChange}
                />
              </Grid>

              {/* Contacto */}
              <Grid item xs={12} sm={4}>
                <Typography sx={typographyStyle}>(*)Contacto:</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  margin="dense"
                  size="small"
                  label=""
                  placeholder="Contacto"
                  required
                  sx={tfSx}
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                />
              </Grid>

              {/* Url */}
              <Grid item xs={12} sm={4}>
                <Typography sx={typographyStyle}>Url:</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  margin="dense"
                  size="small"
                  label=""
                  placeholder="Url"
                  required
                  sx={tfSx}
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                />
              </Grid>

              {/* Coordenadas */}
              <Grid item xs={12} sm={4}>
                <Typography sx={typographyStyle}>Coordenadas:</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  margin="dense"
                  size="small"
                  label=""
                  placeholder="Ej: -27.496797, -64.859053"
                  required
                  sx={tfSx}
                  name="coordinates"
                  value={formData.coordinates}
                  onChange={handleChange}
                />
              </Grid>

              {/* Beneficio */}
              <Grid item xs={12} sm={4}>
                <Typography sx={typographyStyle}>(*)Beneficio:</Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  margin="dense"
                  size="small"
                  label=""
                  placeholder="Beneficio"
                  required
                  sx={tfSx}
                  name="discount"
                  value={formData.discount}
                  onChange={handleChange}
                />
              </Grid>

              {/* Mensajes y acciones */}
              <Grid item xs={12}>
                <Collapse in={openWarning}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Alert
                      sx={{ mb: 2 }}
                      severity="warning"
                      action={
                        <Grid container justifyContent="center" alignItems="center" columnGap={1}>
                          <IconButton
                            aria-label="ok"
                            color="inherit"
                            size="small"
                            onClick={() => { setopenWarning(false); setopenButtons(false); handleSubmit(); }}
                          >
                            <CheckCircleIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton
                            aria-label="cancel"
                            color="inherit"
                            size="small"
                            onClick={() => { setopenWarning(false); setopenButtons(true); }}
                          >
                            <CancelIcon fontSize="inherit" />
                          </IconButton>
                        </Grid>
                      }
                    >
                      ¿Estás seguro de {action}?
                    </Alert>
                  </Box>
                </Collapse>

                <Collapse in={openSuccess}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Alert
                      sx={{ mb: 2 }}
                      severity="success"
                      action={
                        <IconButton
                          aria-label="close"
                          color="inherit"
                          size="small"
                          onClick={() => { setopenButtons(true); setopenSuccess(false); }}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      }
                    >
                      Registro {actionSuccess} correctamente!
                    </Alert>
                  </Box>
                </Collapse>

                <Collapse in={openProgress}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Loading />
                  </Box>
                </Collapse>

                <Collapse in={openError}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Alert
                      sx={{ mb: 2 }}
                      severity="error"
                      action={
                        <IconButton
                          aria-label="close"
                          color="inherit"
                          size="small"
                          onClick={() => { setopenError(false); setopenButtons(true); }}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      }
                    >
                        {(response as any)?.error ?? "Ha ocurrido un error inesperado. Reintente"}
                    </Alert>
                  </Box>
                </Collapse>

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                  <Collapse in={openButtons}>
                    <ButtonBrand
                      variant="contained"
                      onClick={() => { setopenButtons(false); setAction('Editar'); setActionSuccess('editado'); setopenWarning(true); }}
                    >
                      Editar
                    </ButtonBrand>
                  </Collapse>
                  <Collapse in={openButtons}>
                    <ButtonBrand
                      variant="contained"
                      onClick={() => { setopenButtons(false); setAction('Borrar'); setActionSuccess('borrado'); setopenWarning(true); }}
                    >
                      Borrar
                    </ButtonBrand>
                  </Collapse>
                </Box>
              </Grid>
            </Grid>
          </form>
          <Divider />
        </Box>
      </Paper>
    </Box>
  );
}
